import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { Context } from '../../../context/UserContext';

import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';

import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { MaterialReactTable } from 'material-react-table';
import { ThemeProvider, useMediaQuery } from '@mui/material';
/* tema customizado*/
import tableThemeCustom from '../../form/tableThemeCustom';

/* hooks */
import useFlashMessage from '../../../hooks/useFlashMessage';

//Import Material React Table Translations
import CustomDialog from '../../layout/CustomDialog';

import { Box, Button, IconButton, Tooltip, Typography, Dialog, DialogContent } from '@mui/material';
import { Delete, Edit, MonetizationOn } from '@mui/icons-material';

import DuplicidadesProdutos from './duplicidades/Index';
import * as ProdutoAPI from '../../../api/produto';

/* util */
import { obterPermissoesUsuario, printPdf } from '../../../utils/funcoes';
import { podeEditar, podeIncluir, podeCancelar, podeImprimirSintetico } from '../../../hooks/usePermisionMRT';

import { useMRTState } from '../../../hooks/useMRTState';

// helpers
const normalizarCategoriaProduto = (tipo) => {
    const valor = String(tipo || 'produto')
        .trim()
        .toLowerCase();

    const aliases = {
        isbobina: 'bobina',
        'materia-prima': 'materiaprima',
        veiculo: 'vtr',
    };

    return aliases[valor] || valor;
};

const Index = () => {
    const [dados, setDados] = useState([]);
    const [rowSelection, setRowSelection] = useState({});
    const [podeMesclar, setPodeMesclar] = useState(false);
    const [selecaoMesclagem, setSelecaoMesclagem] = useState(null);
    const [mesclando, setMesclando] = useState(false);
    const empresaMesclagem = Number(localStorage.getItem('empresaId'));
    const operadorMesclagem = localStorage.getItem('pessoaId');
    const token = localStorage.getItem('token');
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    //const location = useLocation();
    const urlParams = new URLSearchParams(window.location.search);

    const pathname = '/Produtos/CreateOrEdit';
    const ul = urlParams.get('ul') || 2;
    const li = urlParams.get('li') || 32;
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText') || 'Cadastrar';

    const params = new URLSearchParams({
        ul: ul,
        li: li,
        tipo: tipo,
        operacao: operacaoUUID,
        uuid: uuid,
        btnText: btnText,
    });

    const categoria = normalizarCategoriaProduto(tipo);

    const { parametro } = useContext(Context);

    useEffect(() => {
        let vigente = true;
        setPodeMesclar(false); setRowSelection({}); setSelecaoMesclagem(null);
        if (token && empresaMesclagem) ProdutoAPI.autorizacaoMesclagem({ empresaId: empresaMesclagem })
            .then((r) => { if (vigente) setPodeMesclar(r.mesclarProdutos === true); })
            .catch(() => { if (vigente) setPodeMesclar(false); });
        return () => { vigente = false; };
    }, [token, empresaMesclagem, operadorMesclagem]);

    //dialog customizado
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});

    const mrt = useMRTState({
        storageKey: `produto.index`,
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    // 1. useMemo focado puramente em gerar a string estável dos parâmetros
    const consultaParamsStr = useMemo(() => {
        return JSON.stringify({
            start: pagination.pageIndex * pagination.pageSize,
            size: pagination.pageSize,
            filters: columnFilters ?? [],
            filtersMode: columnFilterFns ?? {},
            globalFilter: globalFilter ?? '',
            sorting: sorting ?? [],
            categoria,
        });
    }, [pagination.pageIndex, pagination.pageSize, columnFilters, columnFilterFns, globalFilter, sorting, categoria]);

    // Objeto real para passar na API (não quebra referência porque vem da string estável)
    const consultaParams = useMemo(() => JSON.parse(consultaParamsStr), [consultaParamsStr]);

    // 2. O fetchData agora é 100% isolado de estados externos
    const fetchData = useCallback(async () => {
        // Usamos uma função dentro do setIsLoading para ler o tamanho atual de 'dados'
        // sem precisar colocar 'dados' ou 'dados.length' nas dependências!
        setIsLoading((prev) => {
            // Se já existem dados, ativa o refetching em vez do loading principal
            // Nota: se preferir simplificar, pode deixar apenas setIsLoading(true) temporariamente
            return true;
        });

        setRowSelection({});
        try {
            const response = await ProdutoAPI.listar(consultaParams);
            setDados(response.data.data);
            setRowCount(response.data.meta.totalRowCount);
            setIsError(false);
        } catch (error) {
            setIsError(true);
            console.error(error);
            setFlashMessage(error.message, 'error');
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [consultaParams]); // <- Única dependência real da requisição

    // 3. EFFECT 1: Cuida estritamente de buscar os dados quando os parâmetros mudam
    useEffect(() => {
        if (!token) return;
        fetchData();
    }, [token, fetchData]); // Só roda se o token mudar ou se os parâmetros (fetchData) mudarem

    // 4. EFFECT 2: Cuida do LocalStorage e Permissões (Separado para não misturar fluxos)
    useEffect(() => {
        if (!token) {
            return navigate('/Auth', { replace: true });
        }

        localStorage.setItem('pagination', JSON.stringify({ pageIndex: pagination.pageIndex, pageSize: pagination.pageSize }));
        localStorage.setItem('columnFilterFns', JSON.stringify(columnFilterFns ?? {}));
        localStorage.setItem('columnFilters', JSON.stringify(columnFilters ?? []));
        localStorage.setItem('globalFilter', globalFilter ?? '');
        localStorage.setItem('sorting', JSON.stringify(sorting ?? []));

        obterPermissoesUsuario(localStorage.getItem('pessoaId'), localStorage.getItem('empresaId'), ul, li);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, consultaParamsStr]); // Só roda quando a string dos parâmetros mudar

    //funções do dialog
    const handleOpenDialog = (row) => {
        setOpenDialog(true);
        setSelectedRow(row);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmDialog = async (row) => {
        // Verifique se há uma linha selecionada
        let msgType = 'success';
        if (selectedRow) {
            try {
                const response = await ProdutoAPI.deletar(selectedRow);

                setFlashMessage(response.data.message, msgType);

                // Após a exclusão, chame a função fetchData() novamente para atualizar as unidades
                fetchData();
            } catch (error) {
                msgType = 'error';
                console.error(error);
                setFlashMessage(error.response.data.message, msgType);
            }
        }
        setOpenDialog(false);
    };

    const getColumnsByTipo = (tipo) => {
        if (tipo === 'bobina') {
            return [
                //column definitions...
                {
                    id: 'bobina',
                    header: 'B O B I N A S',
                    columns: [
                        {
                            accessorKey: 'codigo',
                            header: 'Código',
                        },
                        {
                            accessorKey: 'descricao',
                            header: 'Descrição do Produto',
                            size: 250,
                            muiTableBodyCellProps: ({ row, cell }) => ({
                                onClick: () => {
                                    console.info(row.original);
                                },
                            }),
                            Cell: ({ row }) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                    }}
                                >
                                    {row.original.produtoImagems.length > 0 && (
                                        <img
                                            alt=""
                                            height={30}
                                            src={
                                                process.env.REACT_APP_API + '/imagens/' + row.original.produtoImagems[0].descricao
                                            }
                                            loading="lazy"
                                            style={{ borderRadius: '20%' }}
                                        />
                                    )}
                                    <span>{row.original.descricao}</span>
                                </Box>
                            ),
                        },
                        {
                            accessorKey: 'bobinaComprimento',
                            header: 'Comprimento',
                            muiTableBodyCellProps: {
                                align: 'right',
                            },
                            accessorFn: (row) =>
                                row.bobinaComprimento === null
                                    ? ''
                                    : parseFloat(row.bobinaComprimento)?.toLocaleString('pt-BR', {
                                          style: 'decimal',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                          minimumIntegerDigits: 1,
                                          useGrouping: true,
                                      }),
                        },
                        {
                            accessorKey: 'bobinaDiametro',
                            header: 'Diâmetro',
                            muiTableBodyCellProps: {
                                align: 'right',
                            },
                            accessorFn: (row) =>
                                row.bobinaDiametro === null
                                    ? ''
                                    : parseFloat(row.bobinaDiametro)?.toLocaleString('pt-BR', {
                                          style: 'decimal',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                          minimumIntegerDigits: 1,
                                          useGrouping: true,
                                      }),
                        },
                        {
                            accessorKey: 'bobinaCor.descricao',
                            header: 'Cor',
                        },
                        {
                            accessorKey: 'bobinaSanfonada',
                            header: 'Sanfonada',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.bobinaSanfonada;
                                return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'bobinaTransparente',
                            header: 'Transparente',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.bobinaTransparente;
                                return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'bobinaTratada',
                            header: 'Tratada',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.bobinaTratada;
                                return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'Empresa.nomeFantasia',
                            header: 'Empresa',
                        },
                    ],
                },
            ];
        }
        if (tipo === 'vtr') {
            return [
                //column definitions...
                {
                    id: 'vtr',
                    header: 'VEÍCULOS DA FROTA',
                    columns: [
                        {
                            accessorKey: 'codigo',
                            header: 'Código',
                        },

                        {
                            accessorKey: 'produtoBens.descricao',
                            header: 'Descrição',
                            muiTableBodyCellProps: ({ row, cell }) => ({
                                onClick: () => {
                                    console.info(row.original);
                                },
                            }),
                        },
                        {
                            accessorKey: 'produtoBens.placa',
                            header: 'Placa',
                        },
                        {
                            accessorKey: 'produtoBens.renavan',
                            header: 'Renavan',
                        },
                        {
                            accessorKey: 'ativo',
                            header: 'Ativo',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.ativo;
                                return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'Empresa.nomeFantasia',
                            header: 'Empresa',
                        },
                    ],
                },
            ];
        }
        if (tipo === 'imovel') {
            return [
                //column definitions...
                {
                    id: 'imovel',
                    header: 'CADASTRO DE IMÓVEIS',
                    columns: [
                        {
                            accessorKey: 'produtoBens.Proprietario.nomePrincipal',
                            header: 'Proprietário',
                        },
                        {
                            accessorKey: 'produtoBens.inscricaoMunicipal',
                            header: 'Inscrição Municipal',
                        },
                        {
                            accessorKey: 'produtoBens.contadorEnergia',
                            header: 'Energia',
                        },
                        {
                            accessorKey: 'produtoBens.contadorAgua',
                            header: 'Água',
                        },
                        {
                            accessorKey: 'produtoBens.descricao',
                            header: 'Descrição',
                            muiTableBodyCellProps: ({ row, cell }) => ({
                                onClick: () => {
                                    console.info(row.original);
                                },
                            }),
                        },
                        {
                            accessorKey: 'ativo',
                            header: 'Ativo',
                            type: 'boolean',
                            columnFilterModeOptions: ['equals'],
                            filterSelectOptions: [
                                { label: 'Todos', value: '' },
                                { label: 'Sim', value: '1' },
                                { label: 'Não', value: '0' },
                            ],
                            filterVariant: 'select',
                            Cell: ({ row }) => {
                                const value = row.original.ativo;
                                return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                            },
                        },
                        {
                            accessorKey: 'Empresa.nomeFantasia',
                            header: 'Empresa',
                        },
                    ],
                },
            ];
        }
        //tipo padrão
        return [
            //column definitions...
            {
                id: 'Produtos',
                header: 'P R O D U T O S',
                columns: [
                    {
                        accessorKey: 'codigo',
                        header: 'Código',
                    },
                    {
                        accessorKey: 'codigoFabricante',
                        header: 'Cód Fabricante',
                    },
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição do Produto',
                        size: 250,
                        muiTableBodyCellProps: ({ row, cell }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                        Cell: ({ row }) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                }}
                            >
                                {row.original.produtoImagems.length > 0 && (
                                    <img
                                        alt=""
                                        height={30}
                                        src={process.env.REACT_APP_API + '/imagens/' + row.original.produtoImagems[0].descricao}
                                        loading="lazy"
                                        style={{ borderRadius: '20%' }}
                                    />
                                )}
                                <span>{row.original.descricao}</span>
                            </Box>
                        ),
                    },
                    {
                        accessorKey: 'compra',
                        header: 'Compra',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.compra;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'venda',
                        header: 'Venda',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.venda;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'Fabricante.descricao',
                        header: 'Fabricante',
                    },
                    {
                        accessorKey: 'NCM.ncmFormatado',
                        header: 'NCM',
                    },
                ],
            },
        ];
    };

    const columns = useMemo(() => {
        if (!tipo) return [];
        return getColumnsByTipo(tipo);
    }, [tipo]);

    /* modo tema */
    const theme = tableThemeCustom();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        isMobile,
    });

    return (
        <div>
            {' '}
            <div>
                {openDialog && (
                    <CustomDialog
                        title="Delete PRODUTO!"
                        content="Você tem certeza?"
                        onClose={handleCloseDialog}
                        onConfirm={handleConfirmDialog}
                    />
                )}
            </div>
            <Dialog open={!!selecaoMesclagem} fullWidth maxWidth="lg"
                disableEscapeKeyDown={mesclando}
                onClose={() => { if (!mesclando) setSelecaoMesclagem(null); }}>
                <DialogContent sx={{ p: { xs: 0, md: 1 } }}>
                    {selecaoMesclagem && <DuplicidadesProdutos
                        empresaInicial={empresaMesclagem}
                        selecaoInicial={selecaoMesclagem}
                        onGravando={setMesclando}
                        onFechar={() => { if (!mesclando) setSelecaoMesclagem(null); }}
                        onConcluida={(resultado) => {
                            setSelecaoMesclagem(null); setRowSelection({}); setMesclando(false);
                            setFlashMessage(`Mesclagem concluída. Produto principal #${resultado.principalId}. Auditoria ${resultado.auditoriaId}.`, 'success');
                            fetchData();
                        }}
                    />}
                </DialogContent>
            </Dialog>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    enableRowSelection={(row) => podeMesclar && Number(row.original.ativo) === 1 && Number.isSafeInteger(Number(row.original.id)) && Number(row.original.id) > 0}
                    enableSelectAll={false}
                    onRowSelectionChange={setRowSelection}
                    state={{ ...mrtDefault.state, rowSelection }}
                    renderRowActions={({ row, table }) => (
                        <Box sx={{ display: 'flex', gap: '1rem' }}>
                            <Tooltip arrow placement="left" title="Editar">
                                <span>
                                    {' '}
                                    <IconButton
                                        onClick={() => {
                                            params.set('uuid', row.id);
                                            params.set('btnText', 'Atualizar');
                                            navigate(pathname, params);
                                        }}
                                        disabled={!podeEditar()}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip arrow placement="right" title="Deletar">
                                <span>
                                    <IconButton color="error" onClick={() => handleOpenDialog(row)} disabled={!podeCancelar()}>
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    renderTopToolbarCustomActions={({ table }) => {
                        const selecionados = table.getSelectedRowModel().rows.map((r) => r.original);
                        const idsSelecionados = selecionados.map((p) => Number(p.id));
                        const mesmaEmpresa = new Set(selecionados.map((p) => Number(p.empresaId))).size === 1;
                        const handleImpimir = async () => {
                            const urlSearchParams = new URLSearchParams({
                                start: String(consultaParams.start ?? 0),
                                size: String(consultaParams.size ?? 10),
                                filters: JSON.stringify(consultaParams.filters ?? []),
                                filtersMode: JSON.stringify(consultaParams.filtersMode ?? {}),
                                globalFilter: consultaParams.globalFilter ?? '',
                                sorting: JSON.stringify(consultaParams.sorting ?? []),
                                categoria: consultaParams.categoria ?? '',
                            });

                            const response = await ProdutoAPI.relatorio(urlSearchParams, {});

                            printPdf(response.data);
                        };

                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                {podeMesclar && <Button variant="outlined" onClick={() => navigate('/Produtos/Duplicidades', params)} title="Identificar duplicidades, comparar cadastros e gerar prévia">
                                    Revisar duplicidades
                                </Button>}
                                {podeMesclar && <Button variant="contained" color="warning"
                                    title="Selecione de 2 a 20 produtos da mesma empresa nesta página. A seleção é limpa ao atualizar a lista."
                                    disabled={mrt.isLoading || idsSelecionados.length < 2 || idsSelecionados.length > 20 || !mesmaEmpresa}
                                    onClick={() => setSelecaoMesclagem(idsSelecionados)}>
                                    Mesclar produtos ({idsSelecionados.length})
                                </Button>}
                                <Tooltip arrow placement="top-end" title={`Novo Registro`}>
                                    <div>
                                        <Button
                                            color="secondary"
                                            onClick={() => {
                                                params.delete('uuid');
                                                params.set('btnText', 'Cadastrar');
                                                navigate(pathname, params);
                                            }}
                                            variant="contained"
                                            disabled={!podeIncluir()}
                                        >
                                            Novo
                                        </Button>
                                    </div>
                                </Tooltip>
                                <Tooltip arrow placement="top-end" title={`Imprimir Produtos listagem simples`}>
                                    <div>
                                        <span>
                                            <Button
                                                color="info"
                                                variant="contained"
                                                disabled={!podeImprimirSintetico()}
                                                onClick={() => {
                                                    handleImpimir();
                                                }}
                                            >
                                                Imprimir
                                            </Button>
                                        </span>
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    }}
                    renderDetailPanel={({ row }) => {
                        const produto = row.original;
                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Imagem do produto, se existir */}
                                {Array.isArray(produto.produtoImagems) && produto.produtoImagems.length > 0 && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'start',
                                            alignItems: 'center',
                                            gap: 3,
                                            p: 1,
                                        }}
                                    >
                                        <img
                                            alt="imagem do produto"
                                            height={160}
                                            src={`${process.env.REACT_APP_API}/imagens/${produto.produtoImagems[0].descricao}`}
                                            loading="lazy"
                                            style={{ borderRadius: '10px', border: '1px solid #ccc' }}
                                        />
                                        <Box sx={{ textAlign: 'left' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {produto.descricao}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* Tabela de preços agrupadas por descrição */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {Array.isArray(row.original.tabelaPrecoItems) &&
                                        Object.values(
                                            row.original.tabelaPrecoItems.reduce((acc, item) => {
                                                const descricao = item.tabelaPreco?.descricao || 'Sem tabela';
                                                if (!acc[descricao]) {
                                                    acc[descricao] = {
                                                        tabelaDescricao: descricao,
                                                        itens: [],
                                                    };
                                                }
                                                acc[descricao].itens.push(item);
                                                return acc;
                                            }, {})
                                        ).map((tabela, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    mt: 2,
                                                    border: '1px solid #ccc',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {/* Cabeçalho */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        backgroundColor: '#2e2e2e',
                                                        color: '#fff',
                                                        p: 1,
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 2 }}>Empresa</Box>
                                                    <Box sx={{ flex: 2 }}>Tabela de Preço</Box>
                                                    <Box sx={{ flex: 1, textAlign: 'left', ml: 2 }}>Embalagem</Box>
                                                    <Box sx={{ flex: 1, textAlign: 'right', ml: 2 }}>Fator</Box>
                                                    <Box sx={{ flex: 1, textAlign: 'right' }}>Preço Unitário</Box>
                                                    <Box sx={{ flex: 1, textAlign: 'right' }}>Preço</Box>
                                                </Box>

                                                {/* Linhas */}
                                                {tabela.itens.map((item, j) => (
                                                    <Box
                                                        key={j}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            backgroundColor: j % 2 === 0 ? '#424242' : '#3a3a3a',
                                                            color: '#fff',
                                                            p: 1,
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 2 }}>{item.Empresa?.nomeFantasia || '—'}</Box>
                                                        <Box sx={{ flex: 2 }}>{item.tabelaPreco?.descricao || '—'}</Box>
                                                        <Box sx={{ flex: 1, textAlign: 'left', ml: 2 }}>
                                                            {item?.Embalagem?.descricao || '—'}
                                                        </Box>
                                                        <Box sx={{ flex: 1, textAlign: 'right', ml: 2 }}>{item.fator}</Box>
                                                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                                                            {parseFloat(item.precoUnitario).toLocaleString('pt-BR', {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            })}
                                                        </Box>
                                                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                                                            {parseFloat(item.preco).toLocaleString('pt-BR', {
                                                                style: 'currency',
                                                                currency: 'BRL',
                                                            })}
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ))}
                                </Box>
                            </Box>
                        );
                    }}
                />
            </ThemeProvider>
        </div>
    );
};

export default Index;
